import { memo } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MathUtils } from 'three';

import { AngleIcon } from '@/assets/icons';
import { NumberInput } from '@/modules/common/components/inputs';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { NumUtils } from '@/modules/common/utils';
import { roundToDecimals } from '@/modules/common/helpers/math';

import { ArrangeTransform } from './helpers';
import { GAP_XS, GRID_COLUMNS, INPUT_XS, LABEL_XS } from './layout';
import { TransformButtons } from './TransformButtons';
import { DimensionKind, EditMode, Geometry } from './types';

type Props = {
  kind: DimensionKind;
  geometry: Geometry;
  selectedIds: string[];
  editMode: EditMode;
  disabled: boolean;
  onXChange: (value: number) => void;
  onYChange: (value: number) => void;
  onLengthChange: (value: number) => void;
  onWidthChange: (value: number) => void;
  onAngleChange: (value: number) => void;
  /** The rotate and flip buttons next to the angle */
  canRotate: boolean;
  canFlip: boolean;
  flipDisabledReasonKey?: string;
  onTransform: (transform: ArrangeTransform) => void;
};

type TooltipField = 'x' | 'y' | 'length' | 'width' | 'angle';

const TOOLTIP_WRAPPER_STYLE = { display: 'block', width: '100%' };
const ANGLE_DECIMALS = 2;

/**
 * @description Shows the angle from 0 to 360, while it's stored from -180 to 180. Rounded before
 * wrapping, so an angle just below 0 shows as 0 instead of 360.
 */
const toDisplayAngle = (angleRad: number): number =>
  NumUtils.restrictToCircularRange(
    roundToDecimals(MathUtils.radToDeg(angleRad), ANGLE_DECIMALS),
    360,
  );

/**
 * @description The tooltip for a field, matching what it shows: one item, every item on its own
 * (Mixed when they differ) or the selection as a whole
 */
const getTooltipKey = (
  field: TooltipField,
  kind: DimensionKind,
  editMode: EditMode,
  itemCount: number,
  canRotate: boolean,
): string => {
  const prefix = 'interface:properties.dimensions.tooltips';
  if (field === 'angle' && (kind === 'restricted' || !canRotate)) return `${prefix}.angle_disabled`;
  if (editMode === 'group') return `${prefix}.group.${field}`;
  if (itemCount === 1) return `${prefix}.single.${field}`;
  return `${prefix}.individual.${field}`;
};
function SelectionDimensionFieldsComponent({
  kind,
  geometry,
  selectedIds,
  editMode,
  disabled,
  onXChange,
  onYChange,
  onLengthChange,
  onWidthChange,
  onAngleChange,
  canRotate,
  canFlip,
  flipDisabledReasonKey,
  onTransform,
}: Props) {
  const { t } = useTranslation();
  const angleDisabled = disabled || !geometry.canRotate;
  // A group shows its bounding box size, read-only
  const lengthAndWidthDisabled = disabled || !geometry.canResize || editMode === 'group';
  const unit = t<string>('interface:settings.units.mm', 'mm');
  const selectionKey = `${selectedIds.join('|')}:${editMode}`;
  const debounceTime = 1000;
  const errorType = 'floating';
  const tooltipKey = (field: TooltipField) =>
    getTooltipKey(field, kind, editMode, selectedIds.length, geometry.canRotate);

  return (
    <Grid
      key={selectionKey}
      container
      alignItems='center'
      justifyContent='space-between'
      rowGap={1}
      columns={GRID_COLUMNS}
      sx={{ '&:hover': { cursor: 'default' } }}
    >
      <Grid item xs={LABEL_XS}>
        <Typography fontSize='10px'>X</Typography>
      </Grid>
      <Grid item xs={INPUT_XS}>
        <StyledTooltip titleKey={tooltipKey('x')}>
          <NumberInput
            value={geometry.centerX === null ? null : NumUtils.metersToMmRounded(geometry.centerX)}
            unit={unit}
            disabled={disabled}
            onChange={onXChange}
            debounceMs={debounceTime}
            errorType={errorType}
          />
        </StyledTooltip>
      </Grid>
      <Grid item xs={GAP_XS} />
      <Grid item xs={LABEL_XS}>
        <Typography fontSize='10px'>Y</Typography>
      </Grid>
      <Grid item xs={INPUT_XS}>
        <StyledTooltip titleKey={tooltipKey('y')}>
          <NumberInput
            value={geometry.centerY === null ? null : NumUtils.metersToMmRounded(geometry.centerY)}
            unit={unit}
            disabled={disabled}
            onChange={onYChange}
            debounceMs={debounceTime}
            errorType={errorType}
          />
        </StyledTooltip>
      </Grid>
      <Grid item xs={LABEL_XS}>
        <Typography fontSize='10px' sx={{ opacity: lengthAndWidthDisabled ? 0.5 : 1 }}>
          L
        </Typography>
      </Grid>
      <Grid item xs={INPUT_XS}>
        {' '}
        <StyledTooltip titleKey={tooltipKey('length')}>
          <NumberInput
            value={geometry.length === null ? null : NumUtils.metersToMmRounded(geometry.length)}
            unit={unit}
            disabled={lengthAndWidthDisabled}
            minValue={0}
            onChange={onLengthChange}
            onChangeBlur
            debounceMs={debounceTime}
            errorType={errorType}
          />
        </StyledTooltip>
      </Grid>
      <Grid item xs={GAP_XS} />
      <Grid item xs={LABEL_XS}>
        <Typography fontSize='10px' sx={{ opacity: lengthAndWidthDisabled ? 0.5 : 1 }}>
          W
        </Typography>
      </Grid>
      <Grid item xs={INPUT_XS}>
        <StyledTooltip titleKey={tooltipKey('width')}>
          <NumberInput
            value={geometry.width === null ? null : NumUtils.metersToMmRounded(geometry.width)}
            unit={unit}
            disabled={lengthAndWidthDisabled}
            minValue={0}
            onChange={onWidthChange}
            onChangeBlur
            debounceMs={debounceTime}
            errorType={errorType}
          />
        </StyledTooltip>
      </Grid>
      <Grid item xs={LABEL_XS} sx={{ opacity: angleDisabled ? 0.5 : 1 }}>
        <AngleIcon style={{ height: '16px', width: '16px' }} />
      </Grid>
      <Grid item xs={INPUT_XS} sx={{ marginRight: 'auto' }}>
        <StyledTooltip titleKey={tooltipKey('angle')}>
          {/* span: a disabled input fires no mouse events, and the tooltip explains why it's disabled */}
          <span style={TOOLTIP_WRAPPER_STYLE}>
            <NumberInput
              value={geometry.angle === null ? null : toDisplayAngle(geometry.angle)}
              decimals={ANGLE_DECIMALS}
              unit='°'
              disabled={angleDisabled}
              onChange={onAngleChange}
              debounceMs={debounceTime}
              errorType={errorType}
            />
          </span>
        </StyledTooltip>
      </Grid>
      <Grid item xs={GAP_XS} />
      <Grid item xs={LABEL_XS} />
      <Grid item xs={INPUT_XS}>
        <TransformButtons
          canRotate={canRotate}
          canFlip={canFlip}
          flipDisabledReasonKey={flipDisabledReasonKey}
          onTransform={onTransform}
        />
      </Grid>
    </Grid>
  );
}

export const SelectionDimensionFields = memo(SelectionDimensionFieldsComponent);
